import React from 'react';

export type TagColor = 'gray' | 'red' | 'yellow' | 'green' | 'blue' | 'indigo' | 'purple' | 'pink' | 'orange';

export type TagSize = 'xs' | 'sm' | 'base';

export interface TagBadgeProps {
  label: React.ReactNode;
  color?: TagColor;
  size?: TagSize;
  onRemove?: () => void;
  className?: string;
}

export default function TagBadge({
  label,
  color = 'gray',
  size = 'xs',
  onRemove,
  className = ''
}: TagBadgeProps) {
  const getColorClasses = (color: TagColor) => {
    switch (color) {
      case 'red':
        return {
          bg: 'bg-red-50 dark:bg-red-900/20',
          text: 'text-red-700 dark:text-red-300',
          ring: 'ring-red-600/10 dark:ring-red-500/30',
          button: 'hover:bg-red-600/20 dark:hover:bg-red-500/30',
          stroke: 'stroke-red-600/50 group-hover:stroke-red-600/75 dark:stroke-red-400/50 dark:group-hover:stroke-red-400/75'
        };
      case 'yellow':
        return {
          bg: 'bg-yellow-50 dark:bg-yellow-900/20',
          text: 'text-yellow-800 dark:text-yellow-300',
          ring: 'ring-yellow-600/20 dark:ring-yellow-500/30',
          button: 'hover:bg-yellow-600/20 dark:hover:bg-yellow-500/30',
          stroke: 'stroke-yellow-700/50 group-hover:stroke-yellow-700/75 dark:stroke-yellow-400/50 dark:group-hover:stroke-yellow-400/75'
        };
      case 'green':
        return {
          bg: 'bg-green-50 dark:bg-green-900/20',
          text: 'text-green-700 dark:text-green-300',
          ring: 'ring-green-600/20 dark:ring-green-500/30',
          button: 'hover:bg-green-600/20 dark:hover:bg-green-500/30',
          stroke: 'stroke-green-700/50 group-hover:stroke-green-700/75 dark:stroke-green-400/50 dark:group-hover:stroke-green-400/75'
        };
      case 'blue':
        return {
          bg: 'bg-blue-50 dark:bg-blue-900/20',
          text: 'text-blue-700 dark:text-blue-300',
          ring: 'ring-blue-700/10 dark:ring-blue-500/30',
          button: 'hover:bg-blue-600/20 dark:hover:bg-blue-500/30',
          stroke: 'stroke-blue-700/50 group-hover:stroke-blue-700/75 dark:stroke-blue-400/50 dark:group-hover:stroke-blue-400/75'
        };
      case 'indigo':
        return {
          bg: 'bg-indigo-50 dark:bg-indigo-900/20',
          text: 'text-indigo-700 dark:text-indigo-300',
          ring: 'ring-indigo-700/10 dark:ring-indigo-500/30',
          button: 'hover:bg-indigo-600/20 dark:hover:bg-indigo-500/30',
          stroke: 'stroke-indigo-600/50 group-hover:stroke-indigo-600/75 dark:stroke-indigo-400/50 dark:group-hover:stroke-indigo-400/75'
        };
      case 'purple':
        return {
          bg: 'bg-purple-50 dark:bg-purple-900/20',
          text: 'text-purple-700 dark:text-purple-300',
          ring: 'ring-purple-700/10 dark:ring-purple-500/30',
          button: 'hover:bg-purple-600/20 dark:hover:bg-purple-500/30',
          stroke: 'stroke-violet-600/50 group-hover:stroke-violet-600/75 dark:stroke-purple-400/50 dark:group-hover:stroke-purple-400/75'
        };
      case 'pink':
        return {
          bg: 'bg-pink-50 dark:bg-pink-900/20',
          text: 'text-pink-700 dark:text-pink-300',
          ring: 'ring-pink-700/10 dark:ring-pink-500/30',
          button: 'hover:bg-pink-600/20 dark:hover:bg-pink-500/30',
          stroke: 'stroke-pink-700/50 group-hover:stroke-pink-700/75 dark:stroke-pink-400/50 dark:group-hover:stroke-pink-400/75'
        };
      case 'orange':
        return {
          bg: 'bg-orange-50 dark:bg-orange-900/20',
          text: 'text-orange-700 dark:text-orange-300',
          ring: 'ring-orange-700/10 dark:ring-orange-500/30',
          button: 'hover:bg-orange-600/20 dark:hover:bg-orange-500/30',
          stroke: 'stroke-orange-700/50 group-hover:stroke-orange-700/75 dark:stroke-orange-400/50 dark:group-hover:stroke-orange-400/75'
        };
      case 'gray':
      default:
        return {
          bg: 'bg-gray-50 dark:bg-gray-800',
          text: 'text-gray-600 dark:text-gray-300',
          ring: 'ring-gray-500/10 dark:ring-gray-600/30',
          button: 'hover:bg-gray-500/20 dark:hover:bg-gray-600/30',
          stroke: 'stroke-gray-600/50 group-hover:stroke-gray-600/75 dark:stroke-gray-400/50 dark:group-hover:stroke-gray-400/75'
        };
    }
  };

  const colors = getColorClasses(color);

  const getSizeClasses = (size: TagSize) => {
    switch (size) {
      case 'sm':
        return 'px-1.5 py-0.5 text-[0.625rem]';
      case 'base':
        return 'px-2.5 py-1.5 text-sm';
      case 'xs':
      default:
        return 'px-2 py-1 text-xs';
    }
  };

  const sizeClasses = getSizeClasses(size);

  return (
    <span className={`inline-flex items-center gap-x-0.5 rounded-md ${sizeClasses} font-medium ring-1 ring-inset ${colors.bg} ${colors.text} ${colors.ring} ${className}`}>
      {label}
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          className={`group relative -mr-1 h-3.5 w-3.5 rounded-sm ${colors.button}`}
        >
          <span className="sr-only">Remove</span>
          <svg viewBox="0 0 14 14" className={`h-3.5 w-3.5 ${colors.stroke}`} fill="none" strokeWidth="1.5">
            <path d="M4 4l6 6m0-6l-6 6" />
          </svg>
          <span className="absolute -inset-1"></span>
        </button>
      )}
    </span>
  );
}
